import { computed, nextTick, readonly, ref } from "vue"

export interface TutorialStep {
    id: string
    target?: string
    title?: string
    description: string
    canClick?: boolean
    middleware?: Middleware
}

export interface Tutorial {
    id: string
    title?: string
    steps: TutorialStep[]
}

export type TutorialStepHook = () => void | Promise<void>

// before and after middleware functions can be used to perform actions before or after a tutorial step is shown.
export interface Middleware {
    before?: TutorialStepHook
    after?: TutorialStepHook
}

const tutorial = ref<Tutorial>()
const stepIndex = ref(0)
const running = ref(false)
const elementsVersion = ref(0)
const transitioning = ref(false)

const elements = new Map<string, HTMLElement>()

function hasTarget(step?: TutorialStep) {
    return step?.target != null && elements.has(step.target)
}

function canResolveStep(step?: TutorialStep) {
    return step != null && (step.target == null || hasTarget(step) || step.middleware?.before != null)
}

function findStepIndex(from: number, direction: 1 | -1) {
    const steps = tutorial.value?.steps ?? []

    for (let index = from; index >= 0 && index < steps.length; index += direction) {
        if (canResolveStep(steps[index]))
            return index
    }

    return -1
}

async function runMiddleware(step: TutorialStep | undefined, stage: "before" | "after") {
    const action = step?.middleware?.[stage]

    if (!action)
        return

    try {
        await action()
    } catch (error) {
        console.error(`Tutorial ${stage} middleware failed for step "${step?.id}"`, error)
    }
}

async function activateStep(index: number, direction: 1 | -1 = 1) {
    const steps = tutorial.value?.steps ?? []
    const step = steps[index]

    if (!step)
        return false

    await runMiddleware(step, "before")
    await nextTick()

    if (step.target != null && !hasTarget(step)) {
        const fallbackIndex = findStepIndex(index + direction, direction)

        if (fallbackIndex === -1)
            return false

        return activateStep(fallbackIndex, direction)
    }

    stepIndex.value = index
    running.value = true

    return true
}

async function leaveCurrentStep() {
    if (!tutorial.value || !running.value)
        return

    await runMiddleware(tutorial.value.steps[stepIndex.value], "after")
}

function register(id: string, el: HTMLElement) {
    elements.set(id, el)
    elementsVersion.value++

    if (!tutorial.value || running.value || transitioning.value)
        return

    const firstValidStep = findStepIndex(stepIndex.value, 1)

    if (firstValidStep !== -1) {
        transitioning.value = true
        void activateStep(firstValidStep).finally(() => {
            transitioning.value = false
        })
    }
}

function unregister(id: string) {
    elements.delete(id)
    elementsVersion.value++

    if (!tutorial.value || !running.value || transitioning.value)
        return

    const activeStep = tutorial.value.steps[stepIndex.value]

    if (activeStep?.target == null || hasTarget(activeStep))
        return

    const nextStepIndex = findStepIndex(stepIndex.value + 1, 1)

    if (nextStepIndex !== -1) {
        void next()
        return
    }

    const previousStepIndex = findStepIndex(stepIndex.value - 1, -1)

    if (previousStepIndex !== -1) {
        void previous()
        return
    }

    void stop()
}

async function start(value: Tutorial) {
    tutorial.value = value
    transitioning.value = true
    const firstValidStep = findStepIndex(0, 1)

    if (firstValidStep === -1) {
        stepIndex.value = 0
        running.value = false
        transitioning.value = false
        return
    }

    await activateStep(firstValidStep)
    transitioning.value = false
}

async function stop() {
    if (transitioning.value)
        return

    transitioning.value = true
    await leaveCurrentStep()
    tutorial.value = undefined
    stepIndex.value = 0
    running.value = false
    transitioning.value = false
}

async function next() {
    if (!tutorial.value || transitioning.value)
        return

    transitioning.value = true
    await leaveCurrentStep()

    const nextStepIndex = findStepIndex(stepIndex.value + 1, 1)

    if (nextStepIndex === -1) {
        tutorial.value = undefined
        stepIndex.value = 0
        running.value = false
        transitioning.value = false
        return
    }

    const activated = await activateStep(nextStepIndex, 1)

    if (!activated) {
        tutorial.value = undefined
        stepIndex.value = 0
        running.value = false
    }

    transitioning.value = false
}

async function previous() {
    if (transitioning.value)
        return

    const previousStepIndex = findStepIndex(stepIndex.value - 1, -1)

    if (previousStepIndex === -1)
        return

    transitioning.value = true
    await leaveCurrentStep()
    await activateStep(previousStepIndex, -1)
    transitioning.value = false
}

async function completeStep() {
    await next()
}

const currentStep = computed(() =>
    tutorial.value?.steps[stepIndex.value]
)

const currentElement = computed(() => {
    elementsVersion.value
    const step = currentStep.value

    if (!step?.target)
        return

    return elements.get(step.target)
})

const progress = computed(() => ({
    current: stepIndex.value + 1,
    total: tutorial.value?.steps.length ?? 0,
    percent:
        tutorial.value == null
            ? 0
            : ((stepIndex.value + 1) / tutorial.value.steps.length) * 100
}))

export function useTutorial() {
    return {
        register,
        unregister,
        start,
        stop,
        next,
        previous,
        completeStep,
        isRunning: readonly(running),
        currentStep,
        currentElement,
        title: computed(() => tutorial.value?.title),
        progress
    }
}
